import * as THREE from "three/webgpu";
import { VortexWindField, createWindLineSystem, type WindLineSystem } from "three-windline";
import type { BattleTeam } from "./BattleInteractionRouter";

const ZERO = new THREE.Vector3();
const FORWARD = new THREE.Vector3(0, 0, -1);
const MATRIX = new THREE.Matrix4();
const POSITION = new THREE.Vector3();
const QUATERNION = new THREE.Quaternion();
const SCALE = new THREE.Vector3();
const EULER = new THREE.Euler();
const COLOR = new THREE.Color();
export const TORNADO_DURATION = 4.2;
const BODY_COUNT = 180;
const SHELL_COUNT = 190;
const DEBRIS_COUNT = 42;

const WIND_LOOK = {
  dark: 0x0b514d,
  middle: 0x3ccbb2,
  bright: 0xc5fff0,
  debris: [0x84e6bf, 0xf3d27a, 0xb9eee5] as const,
};

function hash32(value: number): number {
  let result = value | 0;
  result = Math.imul(result ^ (result >>> 16), 0x21f0aaad);
  result = Math.imul(result ^ (result >>> 15), 0x735a2d97);
  return (result ^ (result >>> 15)) >>> 0;
}

function hashUnit(value: number): number {
  return hash32(value) / 0xffffffff;
}

/**
 * Battle adapter around the authored Pajama Studio Tornado Game funnel.
 *
 * This recipe intentionally stays in parity with
 * twister/src/client/twister-visual.ts: a faceted radial BODY and an additive
 * camera-facing SHELL share one VortexWindField. The battle layer only owns
 * the target location and finite gift-event lifetime.
 */
export class BattleTornado {
  readonly root = new THREE.Group();
  readonly #field: VortexWindField;
  readonly #body: WindLineSystem;
  readonly #shell: WindLineSystem;
  readonly #base: THREE.Mesh<THREE.RingGeometry, THREE.MeshBasicMaterial>;
  readonly #debris: THREE.InstancedMesh<THREE.IcosahedronGeometry, THREE.MeshToonMaterial>;
  readonly #halos: THREE.InstancedMesh<THREE.TorusGeometry, THREE.MeshBasicMaterial>;
  readonly #debrisTraits = new Float32Array(DEBRIS_COUNT * 5);
  readonly #anchor = new THREE.Vector3();
  #target: BattleTeam = "aqua";
  #startedAt = -10;
  #pulse = 0;

  constructor(scene: THREE.Scene) {
    this.root.name = "gift-tornado";
    scene.add(this.root);

    this.#field = new VortexWindField({
      angularSpeed: 16,
      radialInflow: 2.8,
      lift: 24,
      turbulence: 0.6,
      softeningRadius: 2.4,
      envelope: {
        height: 5.5,
        radius: [0.25, 2.6],
        taperExponent: 0.66,
        shellBias: 0.83,
        coreRadiusRatio: 0.12,
        axisControl: [0.12, -0.08],
        axisTip: [-0.1, 0.14],
        axisWander: 0.18,
      },
    });

    this.#body = createWindLineSystem({
      scene,
      field: this.#field,
      curve: "straight",
      ribbonMode: "radial",
      capacity: 240,
      count: 0,
      segments: 14,
      seed: hash32(0x70ad4ace ^ 0x4db8193f),
      style: {
        length: 9.5,
        widthWorldUnits: [0.14, 0.68],
        colors: [WIND_LOOK.dark, WIND_LOOK.middle],
        colorRandomness: 0.12,
        colorBanding: 0.04,
        opacity: 0.98,
        surfaceRoughness: 0.74,
        surfaceSpecular: 0.24,
        surfaceRim: 0.18,
        surfaceEmission: 0.03,
        surfaceLightDirection: [-0.42, 0.84, -0.34],
        nearFade: [0.2, 0.8],
        farFade: [58, 128],
        lifetime: [3.2, 6.2],
        speed: [4, 19],
        fieldSpeedMultiplier: 1,
        visibilityResponse: 10,
        visibilityThreshold: [0.03, 0.42],
      },
      depthTest: true,
      depthWrite: true,
      renderOrder: 4,
      name: "battle-tornado-radial-body",
    });

    this.#shell = createWindLineSystem({
      scene,
      field: this.#field,
      curve: "straight",
      ribbonMode: "camera",
      capacity: 240,
      count: 0,
      segments: 22,
      seed: hash32(0x70ad4ace ^ 0x71ca55d9),
      style: {
        regionRadius: 5.2,
        verticalHalfSpan: 6.2,
        centerLift: 0,
        forwardBias: 0,
        length: 9.2,
        widthCssPixels: [1.05, 3.5],
        colors: [WIND_LOOK.middle, WIND_LOOK.bright],
        colorRandomness: 0.14,
        colorBanding: 0.08,
        opacity: 0.8,
        curveAmplitude: [0, 0],
        curveFrequency: [0, 0],
        nearFade: [0.4, 1.2],
        farFade: [42, 112],
        lifetime: [2.8, 5.4],
        speed: [4, 18],
        fieldSpeedMultiplier: 1.06,
        visibilityResponse: 10,
        visibilityThreshold: [0.03, 0.5],
      },
      depthTest: true,
      depthWrite: false,
      blending: "additive",
      renderOrder: 6,
      name: "battle-tornado-camera-shell",
    });

    this.#base = new THREE.Mesh(
      new THREE.RingGeometry(0.48, 1, 48),
      new THREE.MeshBasicMaterial({
        color: WIND_LOOK.bright,
        transparent: true,
        opacity: 0.38,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        side: THREE.DoubleSide,
      }),
    );
    this.#base.name = "battle-tornado-ground-ring";
    this.#base.rotation.x = -Math.PI / 2;
    this.#base.position.y = 0.045;
    this.#base.renderOrder = 7;
    this.root.add(this.#base);

    this.#halos = new THREE.InstancedMesh(
      new THREE.TorusGeometry(1, 0.025, 5, 44),
      new THREE.MeshBasicMaterial({
        color: WIND_LOOK.bright,
        transparent: true,
        opacity: 0.48,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      }),
      3,
    );
    this.#halos.name = "battle-tornado-halos";
    this.#halos.frustumCulled = false;
    this.#halos.renderOrder = 7;
    this.root.add(this.#halos);

    this.#debris = new THREE.InstancedMesh(
      new THREE.IcosahedronGeometry(0.11, 0),
      new THREE.MeshToonMaterial({ color: 0xffffff, vertexColors: true }),
      DEBRIS_COUNT,
    );
    this.#debris.name = "battle-tornado-orbiting-debris";
    this.#debris.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.#debris.castShadow = true;
    this.#debris.receiveShadow = true;
    this.#debris.frustumCulled = false;
    for (let index = 0; index < DEBRIS_COUNT; index += 1) {
      const offset = index * 5;
      this.#debrisTraits[offset] = hashUnit(0x70ad4ace + index * 11);
      this.#debrisTraits[offset + 1] = hashUnit(0x70ad4ace + index * 17 + 3);
      this.#debrisTraits[offset + 2] = hashUnit(0x70ad4ace + index * 23 + 7);
      this.#debrisTraits[offset + 3] = hashUnit(0x70ad4ace + index * 31 + 13);
      this.#debrisTraits[offset + 4] = hashUnit(0x70ad4ace + index * 43 + 19);
      this.#debris.setColorAt(index, COLOR.set(WIND_LOOK.debris[index % WIND_LOOK.debris.length]!));
    }
    this.root.add(this.#debris);
    this.root.visible = false;
  }

  trigger(target: BattleTeam, time: number): void {
    this.#target = target;
    this.#startedAt = time;
    this.#pulse = 1.2;
  }

  update(dt: number, time: number, frontline: number, camera: THREE.Camera, reducedMotion: boolean): void {
    const age = time - this.#startedAt;
    const active = age >= 0 && age < TORNADO_DURATION;
    const sign = this.#target === "ember" ? -1 : 1;
    this.#anchor.set(frontline + sign * 1.55, 0.06, 0);
    this.root.position.copy(this.#anchor);
    this.root.visible = active;

    const exit = 1 - THREE.MathUtils.smoothstep(age, TORNADO_DURATION - 0.72, TORNADO_DURATION);
    const motionScale = reducedMotion ? 0.35 : 1;
    this.#field.configure({
      center: this.#anchor,
      baseVelocity: [0, 0, 0],
      angularSpeed: 24.1,
      radialInflow: 3.4,
      lift: 35,
      turbulence: reducedMotion ? 0.14 : 0.38,
      softeningRadius: 2.8,
      envelope: {
        height: 8.6,
        radius: [0.28, 3.65],
        taperExponent: 0.68,
        shellBias: 0.66,
        coreRadiusRatio: 0.12,
        axisControl: reducedMotion ? [0.18, -0.1] : [0.8, -0.42],
        axisTip: reducedMotion ? [-0.12, 0.1] : [-0.58, 0.5],
        axisWander: reducedMotion ? 0.1 : 0.72,
      },
    });
    this.#body.setCount(active ? (reducedMotion ? 82 : BODY_COUNT) : 0);
    this.#shell.setCount(active ? (reducedMotion ? 72 : SHELL_COUNT) : 0);
    const frame = {
      timeSeconds: time,
      deltaSeconds: Math.min(dt, 0.08),
      anchor: this.#anchor,
      camera,
      observerVelocity: ZERO,
      forward: FORWARD,
      active,
      intensity: active ? (0.9 + this.#pulse * 0.28) * exit : 0,
    };
    this.#body.update(frame);
    this.#shell.update(frame);

    this.#pulse *= Math.exp(-dt * 4.8);
    if (active) {
      const pulse = this.#pulse;
      this.#base.scale.setScalar((2.55 + pulse * 1.4) * exit);
      this.#base.rotation.z = time * 2.8;
      this.#base.material.opacity = (0.22 + pulse * 0.42) * exit;
      this.#updateHalos(time, pulse, exit);
      this.#updateDebris(time, reducedMotion ? 16 : DEBRIS_COUNT, pulse, motionScale, exit);
    }
  }

  dispose(): void {
    this.#body.dispose();
    this.#shell.dispose();
    this.#base.geometry.dispose();
    this.#base.material.dispose();
    this.#debris.geometry.dispose();
    this.#debris.material.dispose();
    this.#halos.geometry.dispose();
    this.#halos.material.dispose();
    this.root.removeFromParent();
  }

  #updateHalos(time: number, pulse: number, exit: number): void {
    for (let index = 0; index < this.#halos.count; index += 1) {
      const phase = index / this.#halos.count;
      const height = 0.65 + phase * 3.15;
      const radius = 0.66 + phase * 1.45 + pulse * 0.16;
      POSITION.set(0, height, 0);
      EULER.set(
        Math.PI / 2 + Math.sin(time * 1.7 + index) * 0.12,
        0,
        time * 1.36 * (index % 2 === 0 ? 1 : -1),
      );
      QUATERNION.setFromEuler(EULER);
      SCALE.setScalar(radius);
      MATRIX.compose(POSITION, QUATERNION, SCALE);
      this.#halos.setMatrixAt(index, MATRIX);
    }
    this.#halos.instanceMatrix.needsUpdate = true;
    this.#halos.material.opacity = (0.26 + pulse * 0.32) * exit;
  }

  #updateDebris(time: number, count: number, pulse: number, motionScale: number, exit: number): void {
    this.#debris.count = count;
    for (let index = 0; index < count; index += 1) {
      const offset = index * 5;
      const radialSeed = this.#debrisTraits[offset] ?? 0;
      const heightSeed = this.#debrisTraits[offset + 1] ?? 0;
      const phaseSeed = this.#debrisTraits[offset + 2] ?? 0;
      const scaleSeed = this.#debrisTraits[offset + 3] ?? 0;
      const wobbleSeed = this.#debrisTraits[offset + 4] ?? 0;
      const height = 0.3 + heightSeed * 4.4;
      const envelope = 0.46 + Math.pow(height / 4.7, 0.7) * 1.8;
      const angle = phaseSeed * Math.PI * 2 + time * 4.3 * (0.78 + radialSeed * 0.55) * motionScale;
      const radius = envelope * (0.74 + radialSeed * 0.44) * (1 + pulse * (0.16 + wobbleSeed * 0.2));
      POSITION.set(
        Math.cos(angle) * radius,
        height + Math.sin(time * 3.1 + phaseSeed * 19) * 0.16,
        Math.sin(angle) * radius,
      );
      EULER.set(
        time * (1.4 + radialSeed * 2.2) * motionScale,
        angle + time * 0.6 * motionScale,
        time * (0.8 + wobbleSeed * 2) * motionScale,
      );
      QUATERNION.setFromEuler(EULER);
      const itemScale = (0.58 + scaleSeed * 1.4 + pulse * 0.18) * exit;
      SCALE.set(itemScale, itemScale * (0.65 + wobbleSeed * 0.7), itemScale);
      MATRIX.compose(POSITION, QUATERNION, SCALE);
      this.#debris.setMatrixAt(index, MATRIX);
    }
    this.#debris.instanceMatrix.needsUpdate = true;
  }
}
